/** Data display blocks (owner feedback F21): records and numbers shown as cards, boards, timelines, schedules and gauges. */
import { L, P, TONE_LABELS, defineBlock } from './custom-blocks';
import { LOAD, MEETINGS, PEOPLE, POSTS, SERIES, SPLIT, WORK, ex } from './custom-blocks-kit';

const ROWS = ['records', 'static', 'insight'] as const;
const RECORDS = ['records', 'static'] as const;
const row = (en: string, de: string, def: string, o: { opt?: boolean; expect?: 'text' | 'number' | 'date' | 'any' | 'boolean' } = {}) =>
  P.expr(en, de, { scope: 'row', expect: o.expect ?? 'text', ...(o.opt ? { opt: true as const } : { def }) });
const rowText = (en: string, de: string, def?: string) =>
  def === undefined ? P.tpl(en, de, { scope: 'row', opt: true }) : P.tpl(en, de, { scope: 'row', def });

export const dataBlocks = {
  recordcard: defineBlock({
    name: L('Record card', 'Datensatz-Karte'),
    desc: L('One record as a card: title, a line of detail, a badge and a few facts. Click it to open the record.', 'Ein Datensatz als Karte: Titel, eine Detailzeile, eine Marke und einige Fakten. Ein Klick öffnet den Datensatz.'),
    family: 'data',
    icon: 'box',
    keywords: ['card', 'profile', 'summary', 'detail', 'tile', 'contact'],
    summary: 'Shows one record (the first row of source, or the row of an enclosing repeat) as a card with title, subtitle, badge and label/value facts.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Record', 'Datensatz', ['record', 'records', 'static'], { opt: true, hint: L('Leave empty inside a repeat to show its current record.', 'In einer Wiederholung leer lassen, um deren aktuellen Datensatz zu zeigen.') }),
      title: rowText('Title', 'Titel', '{{row.title}}'),
      subtitle: rowText('Second line', 'Zweite Zeile'),
      badge: rowText('Badge', 'Marke'),
      badgeTone: P.tone('Badge colour', 'Farbe der Marke', 'accent'),
      image: row('Picture', 'Bild', '', { opt: true, expect: 'any' }),
      items: P.items(
        'Facts',
        'Fakten',
        { label: P.tpl('Label', 'Bezeichnung', { def: '' }), value: P.tpl('Value', 'Wert', { scope: 'row', def: '' }) },
        { titleKey: 'label', max: 12, def: [], addLabel: L('Add a fact', 'Fakt hinzufügen') },
      ),
      onClick: P.click('When clicked', 'Beim Klick', { scope: 'row' }),
    },
    example: {
      props: {
        source: 'sample',
        title: L('{{data.sample.first.title}}', '{{data.sample.first.title}}'),
        subtitle: L('{{data.sample.first.role}}', '{{data.sample.first.role}}'),
        badge: L('{{data.sample.first.team}}', '{{data.sample.first.team}}'),
        items: [
          { label: L('Status', 'Status'), value: L('{{data.sample.first.status}}', '{{data.sample.first.status}}') },
          { label: L('Score', 'Wert'), value: L('{{data.sample.first.score}} / 100', '{{data.sample.first.score}} / 100') },
        ],
      },
      data: [PEOPLE],
    },
  }),

  kanban: defineBlock({
    name: L('Board', 'Board'),
    desc: L('Cards in columns by status; drag a card to another column to change it.', 'Karten in Spalten nach Status; ziehen Sie eine Karte in eine andere Spalte, um sie zu ändern.'),
    family: 'data',
    icon: 'board',
    keywords: ['kanban', 'columns', 'status board', 'pipeline', 'drag', 'lanes'],
    summary: 'A kanban board. groupBy = expression giving the column key of a row (row.status); columns = [{key,label,tone}] (empty: one column per value). onMove runs when a card is dropped on another column (event.column = the new key, row = the card).',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      groupBy: row('Column of a card', 'Spalte einer Karte', 'row.status'),
      columns: P.items(
        'Columns',
        'Spalten',
        { key: P.str('Value', 'Wert', { def: '', max: 60 }), label: P.tpl('Name', 'Name', { def: '' }), tone: P.tone('Colour', 'Farbe', 'neutral') },
        { titleKey: 'label', max: 12, addLabel: L('Add a column', 'Spalte hinzufügen') },
      ),
      title: rowText('Card title', 'Kartentitel', '{{row.title}}'),
      subtitle: rowText('Second line', 'Zweite Zeile'),
      meta: rowText('Small print', 'Kleingedrucktes'),
      person: row('Person', 'Person', '', { opt: true }),
      onMove: P.call('When a card is moved', 'Wenn eine Karte verschoben wird', { scope: 'row', extras: { column: 'text' } }),
      onClick: P.click('When a card is clicked', 'Beim Klick auf eine Karte', { scope: 'row' }),
      limit: P.num('Cards per column', 'Karten je Spalte', { def: 30, min: 3, max: 200, int: true, advanced: true }),
    },
    example: {
      props: {
        source: 'sample',
        groupBy: 'row.status',
        columns: [
          { key: 'todo', label: L('To do', 'Offen'), tone: 'neutral' },
          { key: 'doing', label: L('In progress', 'In Arbeit'), tone: 'accent' },
          { key: 'done', label: L('Done', 'Erledigt'), tone: 'success' },
        ],
        title: L('{{row.title}}', '{{row.title}}'),
        subtitle: L('{{row.note}}', '{{row.note}}'),
        person: 'row.owner',
      },
      data: [WORK],
    },
  }),

  timeline: defineBlock({
    name: L('Timeline', 'Zeitleiste'),
    desc: L('Events in order along a vertical line, with a date, a title and a few words.', 'Ereignisse in Reihenfolge an einer senkrechten Linie, mit Datum, Titel und wenigen Worten.'),
    family: 'data',
    icon: 'timeline',
    keywords: ['history', 'milestones', 'activity', 'chronology', 'log'],
    summary: 'A vertical timeline: one entry per row, ordered by its date. tone and icon set the dot.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', ROWS),
      date: row('Date', 'Datum', 'row.date', { expect: 'any' }),
      title: rowText('Title', 'Titel', '{{row.title}}'),
      body: rowText('Text', 'Text'),
      tone: row('Colour of the dot', 'Farbe des Punkts', '', { opt: true }),
      icon: P.icon(),
      order: P.pick('Order', 'Reihenfolge', ['desc', 'asc'] as const, 'desc', { seg: true, labels: { desc: L('Newest first', 'Neueste zuerst'), asc: L('Oldest first', 'Älteste zuerst') } }),
      limit: P.num('Entries', 'Einträge', { def: 20, min: 1, max: 200, int: true }),
      onClick: P.click('When an entry is clicked', 'Beim Klick auf einen Eintrag', { scope: 'row' }),
    },
    example: { props: { source: 'sample', date: 'row.date', title: L('{{row.title}}', '{{row.title}}'), body: L('{{row.body}}', '{{row.body}}'), icon: 'check' }, data: [POSTS] },
  }),

  schedule: defineBlock({
    name: L('Schedule', 'Terminplan'),
    desc: L('A week or day view with hours, or an agenda list, for records with a start and an end.', 'Eine Wochen- oder Tagesansicht mit Stunden, oder eine Agenda, für Datensätze mit Beginn und Ende.'),
    family: 'data',
    icon: 'schedule',
    keywords: ['week view', 'day view', 'agenda', 'calendar', 'time grid', 'appointments', 'planner'],
    summary: 'A time-grid calendar. start and end are date or date-time expressions of a row; view = week, day or agenda; anchor = the date to show.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      start: row('Starts', 'Beginn', 'row.start', { expect: 'any' }),
      end: row('Ends', 'Ende', '', { opt: true, expect: 'any' }),
      title: row('Title', 'Titel', 'row.title'),
      toneWhen: row('Colour (neutral, accent, success, warning, danger)', 'Farbe (neutral, accent, success, warning, danger)', '', { opt: true }),
      view: P.pick('View', 'Ansicht', ['week', 'day', 'agenda'] as const, 'week', { labels: { week: L('Week', 'Woche'), day: L('Day', 'Tag'), agenda: L('Agenda', 'Agenda') } }),
      anchor: P.expr('Date to show', 'Anzuzeigendes Datum', { expect: 'date', def: 'today()' }),
      weekStart: P.pick('Week starts on', 'Woche beginnt am', ['monday', 'sunday'] as const, 'monday', { seg: true, labels: { monday: L('Monday', 'Montag'), sunday: L('Sunday', 'Sonntag') } }),
      hours: P.pick('Hours shown', 'Angezeigte Stunden', ['work', 'day', 'all'] as const, 'work', { labels: { work: L('8:00 to 18:00', '8:00 bis 18:00'), day: L('6:00 to 22:00', '6:00 bis 22:00'), all: L('All day', 'Ganzer Tag') } }),
      onClick: P.click('When an event is clicked', 'Beim Klick auf einen Termin', { scope: 'row' }),
    },
    presets: [
      { id: 'week', name: L('Week view', 'Wochenansicht'), icon: 'week', props: { view: 'week' }, keywords: ['time grid'] },
      { id: 'day', name: L('Day view', 'Tagesansicht'), icon: 'clock', props: { view: 'day' } },
      { id: 'agenda', name: L('Agenda', 'Agenda'), icon: 'agenda', props: { view: 'agenda' } },
    ],
    example: { props: { source: 'sample', start: 'row.start', end: 'row.end', title: 'row.title', toneWhen: 'row.status', anchor: 'date("2026-10-12")', view: 'week' }, data: [MEETINGS] },
  }),

  gantt: defineBlock({
    name: L('Gantt chart', 'Gantt-Diagramm'),
    desc: L('Bars over a time axis for work with a start and an end, with progress and today.', 'Balken auf einer Zeitachse für Arbeit mit Beginn und Ende, mit Fortschritt und heute.'),
    family: 'data',
    icon: 'gantt',
    keywords: ['project plan', 'schedule', 'roadmap', 'bars', 'phases'],
    summary: 'A Gantt chart: one bar per row from start to end; progress (0 to 100) fills the bar; today is marked.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      title: row('Name', 'Name', 'row.title'),
      start: row('Starts', 'Beginn', 'row.start', { expect: 'date' }),
      end: row('Ends', 'Ende', 'row.end', { expect: 'date' }),
      progress: row('Progress (0 to 100)', 'Fortschritt (0 bis 100)', '', { opt: true, expect: 'number' }),
      toneWhen: row('Colour (neutral, accent, success, warning, danger)', 'Farbe (neutral, accent, success, warning, danger)', '', { opt: true }),
      label: row('Text next to the bar', 'Text neben dem Balken', '', { opt: true }),
      showToday: P.bool('Mark today', 'Heute markieren', true),
      limit: P.num('Rows', 'Zeilen', { def: 30, min: 1, max: 200, int: true }),
      onClick: P.click('When a bar is clicked', 'Beim Klick auf einen Balken', { scope: 'row' }),
    },
    example: { props: { source: 'sample', title: 'row.title', start: 'row.start', end: 'row.end', progress: 'row.progress', toneWhen: 'if(row.status = "done", "success", "accent")' }, data: [WORK] },
  }),

  tree: defineBlock({
    name: L('Tree', 'Baum'),
    desc: L('A hierarchy you can expand and collapse, built from a parent reference.', 'Eine Hierarchie zum Auf- und Zuklappen, aufgebaut aus einer Elternreferenz.'),
    family: 'data',
    icon: 'branch',
    keywords: ['hierarchy', 'outline', 'nested', 'folders', 'parent child'],
    summary: 'An expandable tree. parent = expression returning the id of a row\'s parent (or empty for a root).',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      parent: row('Parent of a row', 'Elternelement einer Zeile', 'row.parent'),
      title: rowText('Title', 'Titel', '{{row.title}}'),
      meta: rowText('Small print', 'Kleingedrucktes'),
      icon: P.icon(),
      expanded: P.pick('At the start', 'Zu Beginn', ['all', 'roots', 'none'] as const, 'all', { labels: { all: L('All open', 'Alles offen'), roots: L('First level open', 'Erste Ebene offen'), none: L('All closed', 'Alles zu') } }),
      onClick: P.click('When a row is clicked', 'Beim Klick auf eine Zeile', { scope: 'row' }),
    },
    example: { props: { source: 'sample', parent: 'row.parent', title: L('{{row.title}}', '{{row.title}}'), meta: L('{{row.owner}}', '{{row.owner}}'), icon: 'folder' }, data: [WORK] },
  }),

  orgchart: defineBlock({
    name: L('Org chart', 'Organigramm'),
    desc: L('People or teams as boxes joined by lines from the top down.', 'Personen oder Teams als Kästchen, von oben nach unten durch Linien verbunden.'),
    family: 'data',
    icon: 'members',
    keywords: ['organisation', 'hierarchy', 'reporting lines', 'structure', 'team tree'],
    summary: 'An org chart. parent = expression returning the parent row\'s id or title (empty for the top).',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      parent: row('Reports to', 'Berichtet an', 'row.manager'),
      parentBy: P.pick('Parent is given as', 'Elternelement wird angegeben als', ['id', 'title'] as const, 'title', { seg: true, labels: { id: L('Id', 'Kennung'), title: L('Title', 'Titel') } }),
      title: row('Name', 'Name', 'row.title'),
      subtitle: row('Second line', 'Zweite Zeile', '', { opt: true }),
      onClick: P.click('When a box is clicked', 'Beim Klick auf ein Kästchen', { scope: 'row' }),
    },
    example: { props: { source: 'sample', parent: 'row.manager', parentBy: 'title', title: 'row.title', subtitle: 'row.role' }, data: [PEOPLE] },
  }),

  kpi: defineBlock({
    name: L('KPI', 'Kennzahl'),
    desc: L('A big number with a label, a trend against the previous value and a small chart.', 'Eine große Zahl mit Bezeichnung, Trend gegenüber dem Vorwert und kleinem Diagramm.'),
    family: 'data',
    icon: 'trend',
    keywords: ['metric', 'number', 'scorecard', 'headline number', 'indicator'],
    summary: 'A KPI tile: value (a number expression) in a format, an optional previous value for the trend, an optional sparkline from source rows (point = the value of a row).',
    scopes: { row: 'source' },
    props: {
      label: P.tpl('Label', 'Bezeichnung', { def: '' }),
      value: P.expr('Value', 'Wert', { expect: 'any', def: '0' }),
      format: P.format('Format', 'Format'),
      previous: P.expr('Previous value', 'Früherer Wert', { expect: 'number', opt: true }),
      goodWhen: P.pick('Good when', 'Gut, wenn', ['up', 'down'] as const, 'up', { seg: true, labels: { up: L('It goes up', 'Es steigt'), down: L('It goes down', 'Es sinkt') } }),
      comparison: P.tpl('Text after the trend', 'Text nach dem Trend', { opt: true }),
      source: P.source('Chart data', 'Diagrammdaten', ROWS, { opt: true, hint: L('Rows for the small chart.', 'Zeilen für das kleine Diagramm.') }),
      point: row('Value of a point', 'Wert eines Punkts', 'row.value', { expect: 'number' }),
      icon: P.icon(),
      hint: P.tpl('Small print', 'Kleingedrucktes', { opt: true }),
    },
    example: {
      props: { label: L('Hours this week', 'Stunden diese Woche'), value: '37.5', format: { kind: 'number', decimals: 1, unit: L('h', 'h') }, previous: '34', comparison: L('vs. last week', 'ggü. Vorwoche'), source: 'sample', point: 'row.value', icon: 'clock' },
      data: [SERIES],
    },
  }),

  ring: defineBlock({
    name: L('Progress ring', 'Fortschrittsring'),
    desc: L('A circle that fills towards a goal, with the value in the middle.', 'Ein Kreis, der sich zu einem Ziel füllt, mit dem Wert in der Mitte.'),
    family: 'data',
    icon: 'refresh',
    keywords: ['donut', 'circular progress', 'completion', 'percent'],
    summary: 'A ring showing value out of max (number expressions), with the percentage or value in the middle and a label.',
    props: {
      value: P.expr('Value', 'Wert', { expect: 'number', def: '0' }),
      max: P.expr('Goal', 'Ziel', { expect: 'number', def: '100' }),
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      center: P.pick('In the middle', 'In der Mitte', ['percent', 'value', 'none'] as const, 'percent', { labels: { percent: L('Percent', 'Prozent'), value: L('Value', 'Wert'), none: L('Nothing', 'Nichts') } }),
      format: P.format('Format of the value', 'Format des Werts'),
      tone: P.tone('Colour', 'Farbe', 'accent'),
      toneWhen: P.expr('Colour by condition', 'Farbe nach Bedingung', { expect: 'text', opt: true, advanced: true }),
      size: P.num('Size', 'Größe', { def: 112, min: 64, max: 280, int: true, unit: L('px', 'px') }),
    },
    example: { props: { value: '62', max: '100', label: L('Onboarding', 'Onboarding'), center: 'percent' } },
  }),

  gauge: defineBlock({
    name: L('Gauge', 'Tacho'),
    desc: L('A half-circle dial with coloured zones and a needle.', 'Ein Halbkreis-Zifferblatt mit farbigen Bereichen und Zeiger.'),
    family: 'data',
    icon: 'gauge',
    keywords: ['dial', 'speedometer', 'meter', 'thresholds', 'zones'],
    summary: 'A semicircular gauge from min to max with coloured bands; value is a number expression.',
    props: {
      value: P.expr('Value', 'Wert', { expect: 'number', def: '0' }),
      min: P.num('Lowest', 'Niedrigster Wert', { def: 0, int: false }),
      max: P.num('Highest', 'Höchster Wert', { def: 100 }),
      bands: P.items(
        'Zones',
        'Bereiche',
        { upTo: P.num('Up to', 'Bis', { def: 100 }), tone: P.tone('Colour', 'Farbe', 'success') },
        { titleKey: 'upTo', max: 6, def: [], addLabel: L('Add a zone', 'Bereich hinzufügen') },
      ),
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      format: P.format('Format of the value', 'Format des Werts'),
    },
    example: {
      props: {
        value: '72',
        min: 0,
        max: 100,
        label: L('Utilisation', 'Auslastung'),
        bands: [{ upTo: 60, tone: 'success' }, { upTo: 85, tone: 'warning' }, { upTo: 100, tone: 'danger' }],
        format: { kind: 'number', decimals: 0, unit: '%' },
      },
    },
  }),

  sparkline: defineBlock({
    name: L('Sparkline', 'Sparkline'),
    desc: L('A tiny line chart of a series, to sit next to a number.', 'Ein winziges Liniendiagramm einer Reihe, das neben einer Zahl steht.'),
    family: 'data',
    icon: 'pulse',
    keywords: ['trend line', 'mini chart', 'inline chart'],
    summary: 'A small line of the value of each row of source, in order.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', ROWS),
      value: row('Value of a point', 'Wert eines Punkts', 'row.value', { expect: 'number' }),
      tone: P.tone('Colour', 'Farbe', 'accent'),
      fill: P.bool('Fill under the line', 'Fläche unter der Linie', true),
      height: P.num('Height', 'Höhe', { def: 40, min: 16, max: 160, int: true, unit: L('px', 'px') }),
      label: P.tpl('Name for screen readers', 'Name für Screenreader', { opt: true }),
    },
    example: { props: { source: 'sample', value: 'row.value', height: 48 }, data: [SERIES] },
  }),

  heatcal: defineBlock({
    name: L('Heat calendar', 'Heat-Kalender'),
    desc: L('A year-style grid of days shaded by how much happened, like a contribution graph.', 'Ein Raster von Tagen, schattiert nach Aktivität, wie ein Beitragsdiagramm.'),
    family: 'data',
    icon: 'heatcal',
    keywords: ['contribution graph', 'activity', 'streak', 'density', 'github'],
    summary: 'Days in columns of weeks, shaded by the sum, count or average of rows on that day.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      date: row('Date of a row', 'Datum einer Zeile', 'row.date', { expect: 'date' }),
      value: row('Value of a row', 'Wert einer Zeile', 'row.value', { expect: 'number' }),
      agg: P.pick('Combine a day', 'Tag zusammenfassen', ['sum', 'count', 'avg', 'max'] as const, 'sum', { labels: { sum: L('Sum', 'Summe'), count: L('Count', 'Anzahl'), avg: L('Average', 'Durchschnitt'), max: L('Highest', 'Höchster') } }),
      weeks: P.num('Weeks', 'Wochen', { def: 16, min: 4, max: 53, int: true }),
      until: P.expr('Last day', 'Letzter Tag', { expect: 'date', def: 'today()' }),
      tone: P.pick('Colour', 'Farbe', ['accent', 'success', 'warning', 'danger'] as const, 'accent', { labels: { accent: TONE_LABELS.accent, success: TONE_LABELS.success, warning: TONE_LABELS.warning, danger: TONE_LABELS.danger } }),
      onClick: P.call('When a day is clicked', 'Beim Klick auf einen Tag', { extras: { date: 'date', value: 'number' } }),
    },
    example: { props: { source: 'sample', date: 'row.date', value: 'row.value', weeks: 10, until: 'date("2026-09-28")' }, data: [SERIES] },
  }),

  heatmap: defineBlock({
    name: L('Heat map', 'Heatmap'),
    desc: L('A grid of rows and columns whose cells are shaded by a value, e.g. load by person and week.', 'Ein Raster aus Zeilen und Spalten, dessen Zellen nach einem Wert schattiert sind, z. B. Auslastung je Person und Woche.'),
    family: 'data',
    icon: 'heatmap',
    keywords: ['capacity', 'workload', 'matrix', 'intensity', 'planner', 'utilisation'],
    summary: 'A matrix: rows and columns come from two text expressions of each row, the cell value is the sum/average/count/max of a number expression. palette traffic = green to red.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      rowKey: row('Row of a record', 'Zeile eines Datensatzes', 'row.person'),
      columnKey: row('Column of a record', 'Spalte eines Datensatzes', 'row.week'),
      value: row('Value of a record', 'Wert eines Datensatzes', 'row.hours', { expect: 'number' }),
      agg: P.pick('Combine', 'Zusammenfassen', ['sum', 'avg', 'count', 'max'] as const, 'sum', { labels: { sum: L('Sum', 'Summe'), avg: L('Average', 'Durchschnitt'), count: L('Count', 'Anzahl'), max: L('Highest', 'Höchster') } }),
      palette: P.pick('Colours', 'Farben', ['accent', 'traffic'] as const, 'accent', { seg: true, labels: { accent: L('Accent', 'Akzent'), traffic: L('Green to red', 'Grün zu Rot') } }),
      lowAt: P.num('Low until', 'Niedrig bis', { def: 0, advanced: true, hint: L('With "Green to red": cells up to this are green.', 'Bei „Grün zu Rot“: Zellen bis zu diesem Wert sind grün.') }),
      highAt: P.num('High from', 'Hoch ab', { def: 40, advanced: true }),
      format: P.format('Format of a cell', 'Format einer Zelle'),
      showValues: P.bool('Show the numbers', 'Zahlen zeigen', true),
      onCellClick: P.call('When a cell is clicked', 'Beim Klick auf eine Zelle', { extras: { row: 'text', column: 'text', value: 'number' } }),
    },
    example: { props: { source: 'sample', rowKey: 'row.person', columnKey: 'row.week', value: 'row.hours', agg: 'sum', palette: 'traffic', lowAt: 24, highAt: 40, format: { kind: 'number', decimals: 0, unit: L('h', 'h') } }, data: [LOAD] },
  }),

  datagrid: defineBlock({
    name: L('Data grid', 'Datentabelle'),
    desc: L('A table you can sort and search, with checkboxes and cells you edit in place.', 'Eine Tabelle zum Sortieren und Suchen, mit Auswahlkästchen und direkt bearbeitbaren Zellen.'),
    family: 'data',
    icon: 'datagrid',
    keywords: ['spreadsheet', 'sortable table', 'inline edit', 'select rows', 'grid', 'search'],
    summary: 'A sortable, searchable table. columns pick fields of the source; editable cells call onEdit (event.field, event.value, row). selectable keeps the ids of ticked rows in a list state.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      columns: P.items(
        'Columns',
        'Spalten',
        {
          field: P.field('Field', 'Feld', 'source', { def: 'title' }),
          header: P.tpl('Header', 'Kopfzeile', { opt: true }),
          kind: P.pick('Shown as', 'Angezeigt als', ['auto', 'text', 'badge', 'number', 'date', 'progress'] as const, 'auto', {
            labels: { auto: L('As the field is', 'Wie das Feld'), text: L('Text', 'Text'), badge: L('Badge', 'Marke'), number: L('Number', 'Zahl'), date: L('Date', 'Datum'), progress: L('Progress bar', 'Fortschrittsbalken') },
          }),
          editable: P.bool('Editable in place', 'Direkt bearbeitbar', false),
          linkBehavior: P.pick('Linked-record click', 'Klick auf verknüpften Datensatz', ['auto', 'record', 'row', 'none'] as const, 'auto', {
            labels: { auto: L('Use row action when configured', 'Zeilenaktion verwenden, wenn vorhanden'), record: L('Open linked record', 'Verknüpften Datensatz öffnen'), row: L('Use row action', 'Zeilenaktion verwenden'), none: L('Display only', 'Nur anzeigen') },
          }),
          align: P.pick('Alignment', 'Ausrichtung', ['start', 'end'] as const, 'start', { seg: true, labels: { start: L('Left', 'Links'), end: L('Right', 'Rechts') } }),
        },
        { titleKey: 'field', max: 12, def: [{ field: 'title' }], addLabel: L('Add a column', 'Spalte hinzufügen') },
      ),
      search: P.bool('Search box', 'Suchfeld', true),
      sortable: P.bool('Sort by clicking a header', 'Sortieren per Klick auf die Kopfzeile', true),
      selectable: P.bool('Checkboxes', 'Auswahlkästchen', false),
      selection: P.state('Ticked rows are kept in', 'Markierte Zeilen werden gespeichert in', ['list'], { opt: true, when: { prop: 'selectable', in: [true] } }),
      pageSize: P.num('Rows per page', 'Zeilen pro Seite', { def: 10, min: 3, max: 100, int: true }),
      onEdit: P.call('When a cell is edited', 'Wenn eine Zelle bearbeitet wird', { scope: 'row', extras: { field: 'text', value: 'any' } }),
      onRowClick: P.click('When a row is clicked', 'Beim Klick auf eine Zeile', { scope: 'row' }),
      dense: P.bool('Compact rows', 'Kompakte Zeilen', false),
      striped: P.bool('Striped rows', 'Gestreifte Zeilen', false),
      empty: P.tpl('When empty', 'Wenn leer', { opt: true }),
    },
    example: {
      props: {
        source: 'sample',
        columns: [
          { field: 'title', header: L('Name', 'Name') },
          { field: 'role', header: L('Role', 'Rolle') },
          { field: 'team', header: L('Team', 'Team'), kind: 'badge' },
          { field: 'score', header: L('Score', 'Wert'), kind: 'progress', align: 'end' },
        ],
        selectable: true,
        pageSize: 5,
      },
      data: [PEOPLE],
      state: [{ key: 'selected', type: { kind: 'list' } }],
    },
  }),

  leaderboard: defineBlock({
    name: L('Ranking', 'Rangliste'),
    desc: L('A ranked list with bars, rank numbers and avatars.', 'Eine Rangliste mit Balken, Platznummern und Avataren.'),
    family: 'data',
    icon: 'star',
    keywords: ['ranking', 'top', 'scoreboard', 'bars', 'top 5'],
    summary: 'Rows sorted by a number expression, drawn with a bar scaled to the largest value.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', ROWS),
      label: row('Name', 'Name', 'row.title'),
      value: row('Value', 'Wert', 'row.score', { expect: 'number' }),
      format: P.format('Format', 'Format'),
      order: P.pick('Order', 'Reihenfolge', ['desc', 'asc'] as const, 'desc', { seg: true, labels: { desc: L('Highest first', 'Höchster zuerst'), asc: L('Lowest first', 'Niedrigster zuerst') } }),
      max: P.num('Show', 'Anzeigen', { def: 5, min: 1, max: 50, int: true }),
      rank: P.bool('Rank numbers', 'Platznummern', true),
      avatar: P.bool('Avatars', 'Avatare', true),
      onClick: P.click('When a row is clicked', 'Beim Klick auf eine Zeile', { scope: 'row' }),
    },
    example: { props: { source: 'sample', label: 'row.title', value: 'row.score', max: 5 }, data: [PEOPLE] },
  }),

  feed: defineBlock({
    name: L('Activity feed', 'Aktivitätsfeed'),
    desc: L('Short entries with who, what and when, newest first.', 'Kurze Einträge mit Wer, Was und Wann, neueste zuerst.'),
    family: 'data',
    icon: 'history',
    keywords: ['updates', 'stream', 'news', 'changes', 'notifications', 'log'],
    summary: 'A feed of rows: title, body, author and a relative date; icon sets a symbol per row.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', ROWS),
      title: rowText('Title', 'Titel', '{{row.title}}'),
      body: rowText('Text', 'Text'),
      author: row('Author', 'Autor', '', { opt: true }),
      date: row('Date', 'Datum', 'row.date', { expect: 'any' }),
      icon: row('Icon of a row', 'Symbol einer Zeile', '', { opt: true }),
      order: P.pick('Order', 'Reihenfolge', ['desc', 'asc'] as const, 'desc', { seg: true, labels: { desc: L('Newest first', 'Neueste zuerst'), asc: L('Oldest first', 'Älteste zuerst') } }),
      limit: P.num('Entries', 'Einträge', { def: 10, min: 1, max: 100, int: true }),
      onClick: P.click('When an entry is clicked', 'Beim Klick auf einen Eintrag', { scope: 'row' }),
    },
    example: { props: { source: 'sample', title: L('{{row.title}}', '{{row.title}}'), body: L('{{row.body}}', '{{row.body}}'), author: 'row.author', date: 'row.date', icon: 'if(row.kind = "mail", "mail", if(row.kind = "call", "phone", if(row.kind = "success", "check", "pencil")))' }, data: [POSTS] },
  }),

  distribution: defineBlock({
    name: L('Share bar', 'Anteilsbalken'),
    desc: L('One bar split into coloured shares, with a legend and the total.', 'Ein Balken, in farbige Anteile geteilt, mit Legende und Summe.'),
    family: 'data',
    icon: 'sliders',
    keywords: ['stacked bar', 'share', 'breakdown', 'composition', 'split', 'proportion'],
    summary: 'A single stacked bar: one segment per row of source sized by value, coloured by tone (neutral, accent, success, warning, danger).',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', ROWS),
      label: row('Name of a share', 'Name eines Anteils', 'row.title'),
      value: row('Size of a share', 'Größe eines Anteils', 'row.value', { expect: 'number' }),
      tone: row('Colour of a share', 'Farbe eines Anteils', 'row.tone'),
      format: P.format('Format', 'Format'),
      legend: P.bool('Legend', 'Legende', true),
      total: P.bool('Show the total', 'Summe zeigen', true),
    },
    example: { props: { source: 'sample', label: 'row.title', value: 'row.value', tone: 'row.tone' }, data: [SPLIT] },
  }),

  stepper: defineBlock({
    name: L('Progress steps', 'Schrittanzeige'),
    desc: L('A row of named steps that shows where something stands, e.g. an order or an onboarding.', 'Eine Reihe benannter Schritte, die zeigt, wo etwas steht, z. B. eine Bestellung oder ein Onboarding.'),
    family: 'data',
    icon: 'arrow-right',
    keywords: ['status steps', 'tracker', 'stages', 'process', 'checkout'],
    summary: 'Shows steps (label, description) with the step at index current (0-based number, or the label text) highlighted and the earlier ones complete.',
    props: {
      steps: P.items(
        'Steps',
        'Schritte',
        { label: P.tpl('Name', 'Name', { def: '' }), description: P.tpl('Description', 'Beschreibung', { opt: true }) },
        { titleKey: 'label', max: 10, min: 2, def: [{ label: 'Step 1' }, { label: 'Step 2' }], addLabel: L('Add a step', 'Schritt hinzufügen') },
      ),
      current: P.expr('Current step (number, first = 1)', 'Aktueller Schritt (Zahl, erster = 1)', { expect: 'number', def: '1' }),
      direction: P.pick('Direction', 'Richtung', ['horizontal', 'vertical'] as const, 'horizontal', { seg: true, labels: { horizontal: L('Across', 'Quer'), vertical: L('Down', 'Senkrecht') } }),
    },
    example: {
      props: {
        current: '2',
        steps: [
          { label: L('Ordered', 'Bestellt'), description: L('Mon', 'Mo') },
          { label: L('Packed', 'Verpackt'), description: L('Tue', 'Di') },
          { label: L('Shipped', 'Versandt'), description: L('Wed', 'Mi') },
          { label: L('Delivered', 'Geliefert') },
        ],
      },
    },
  }),
};

void ex;
